/**
 * Fire-and-forget auth diagnostics for the Admin dashboard.
 *
 * Mirrors packages/field/src/lib/auth-diag.ts with admin localStorage keys.
 * Never logs email or tokens.
 */
import type { AuthDiagEvent, AuthDiagReason, AuthDiagEventName } from '@cer-autofill/shared';
import { backendBaseUrl, buildId, log } from '../env.js';

const DEVICE_KEY = 'cer-admin-device-id';
const LAST_SESSION_WRITTEN_KEY = 'cer-admin-last-session-written';
const LAST_SESSION_DEVICE_KEY = 'cer-admin-last-session-device-id';
const LAST_USER_SUB_KEY = 'cer-admin-last-user-sub';
const BOOT_LOGGED_KEY = 'cer-admin-auth-diag-boot-logged';
const REFRESH_FAILED_LOGGED_KEY = 'cer-admin-auth-diag-refresh-failed-logged';

function readLocal(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — diagnostics only */
  }
}

function readSessionFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(key: string): void {
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* ignore */
  }
}

function clearSessionFlag(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function userSubFromIdToken(idToken: string | null | undefined): string | null {
  if (!idToken) return null;
  try {
    const [, payload] = idToken.split('.');
    if (!payload) return null;
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(padded)) as { sub?: unknown };
    return typeof decoded.sub === 'string' && decoded.sub.length > 0 ? decoded.sub : null;
  } catch {
    return null;
  }
}

function coarseUaFamily(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  let os = 'Other';
  if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  let browser = 'Other';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome';
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';

  return `${os}/${browser}`.slice(0, 80);
}

function displayMode(): 'standalone' | 'browser' {
  if (typeof window === 'undefined') return 'browser';
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
  } catch {
    /* ignore */
  }
  return 'browser';
}

async function storagePersisted(): Promise<boolean | null> {
  try {
    if (!navigator.storage?.persisted) return null;
    return await navigator.storage.persisted();
  } catch {
    return null;
  }
}

function hoursSinceLastSessionWrite(): number | null {
  const raw = readLocal(LAST_SESSION_WRITTEN_KEY);
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return Math.round(((Date.now() - ms) / 3_600_000) * 100) / 100;
}

function deviceContext(): { deviceIdPresent: boolean; deviceIdLooksNew: boolean } {
  const deviceId = readLocal(DEVICE_KEY);
  const lastDevice = readLocal(LAST_SESSION_DEVICE_KEY);
  const hadSession = Boolean(readLocal(LAST_SESSION_WRITTEN_KEY));
  const deviceIdPresent = Boolean(deviceId);
  const deviceIdLooksNew =
    !deviceIdPresent ||
    (hadSession && Boolean(lastDevice) && lastDevice !== deviceId) ||
    (hadSession && !deviceIdPresent);
  return { deviceIdPresent, deviceIdLooksNew };
}

export function recordSuccessfulSessionWrite(opts: {
  deviceId: string;
  userSub?: string | null;
}): void {
  writeLocal(LAST_SESSION_WRITTEN_KEY, new Date().toISOString());
  writeLocal(LAST_SESSION_DEVICE_KEY, opts.deviceId);
  if (opts.userSub) writeLocal(LAST_USER_SUB_KEY, opts.userSub);
  clearSessionFlag(BOOT_LOGGED_KEY);
  clearSessionFlag(REFRESH_FAILED_LOGGED_KEY);
}

async function buildPayload(
  event: AuthDiagEventName,
  reason: AuthDiagReason,
  userSubOverride?: string | null,
): Promise<AuthDiagEvent> {
  const { deviceIdPresent, deviceIdLooksNew } = deviceContext();
  const persisted = await storagePersisted();
  const storedSub = readLocal(LAST_USER_SUB_KEY);
  return {
    event,
    app: 'admin',
    reason,
    deviceIdPresent,
    deviceIdLooksNew,
    storagePersisted: persisted,
    displayMode: displayMode(),
    uaFamily: coarseUaFamily(),
    appVersion: buildId,
    hoursSinceLastSessionWrite: hoursSinceLastSessionWrite(),
    userSub: userSubOverride ?? storedSub,
  };
}

function postEvent(payload: AuthDiagEvent): void {
  const url = `${backendBaseUrl}/api/diag/auth-event`;
  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch((err: unknown) => log('auth-diag post failed', err));
  } catch (err) {
    log('auth-diag post failed', err);
  }
}

export function reportReauthRequired(
  reason: AuthDiagReason,
  opts?: { userSub?: string | null },
): void {
  if (reason === 'no_session_on_boot') {
    if (readSessionFlag(BOOT_LOGGED_KEY)) return;
    writeSessionFlag(BOOT_LOGGED_KEY);
  }
  if (reason === 'refresh_failed') {
    if (readSessionFlag(REFRESH_FAILED_LOGGED_KEY)) return;
    writeSessionFlag(REFRESH_FAILED_LOGGED_KEY);
  }
  void buildPayload('auth_reauth_required', reason, opts?.userSub).then(postEvent);
}

export function reportSessionCleared(
  reason: 'switch_user' | 'explicit_logout' | 'other',
  opts?: { userSub?: string | null },
): void {
  void buildPayload('session_cleared', reason, opts?.userSub).then(postEvent);
}

export function reportSignInSuccess(opts: { userSub?: string | null }): void {
  void buildPayload('auth_signin_success', 'otp_signin', opts.userSub).then(postEvent);
}
